import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from '@angular/fire/firestore';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  LoadStickyNote,
  StickyNoteAccent,
  StickyNoteVisibility,
} from '../models/sticky-note.model';

@Injectable({ providedIn: 'root' })
export class StickyNoteService {
  private readonly firestore = inject(Firestore);
  private readonly injector = inject(Injector);
  private readonly authService = inject(AuthService);

  private toMillis(ts: any): number {
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    if (typeof ts?.toMillis === 'function') return ts.toMillis();
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
    return 0;
  }

  /**
   * Streams both shared team sticky notes and the signed-in user's personal sticky notes
   * for a load, sorted pinned-first then newest-first.
   */
  watchLoadNotes$(companyId: string | null | undefined, loadId: string | null | undefined): Observable<LoadStickyNote[]> {
    const uid = this.authService.user()?.uid || null;
    if (!companyId || !loadId || !uid) {
      return of([]);
    }

    const colRef = collection(this.firestore, `companies/${companyId}/loadStickyNotes`);

    const teamQuery = query(
      colRef,
      where('loadId', '==', loadId),
      where('visibility', '==', 'team')
    );

    const personalQuery = query(
      colRef,
      where('loadId', '==', loadId),
      where('visibility', '==', 'personal'),
      where('authorId', '==', uid)
    );

    const team$ = runInInjectionContext(
      this.injector,
      () => collectionData(teamQuery, { idField: 'id' }) as Observable<LoadStickyNote[]>
    ).pipe(catchError(() => of([] as LoadStickyNote[])));

    const personal$ = runInInjectionContext(
      this.injector,
      () => collectionData(personalQuery, { idField: 'id' }) as Observable<LoadStickyNote[]>
    ).pipe(catchError(() => of([] as LoadStickyNote[])));

    return combineLatest([team$, personal$]).pipe(
      map(([teamNotes, personalNotes]) => {
        const merged = new Map<string, LoadStickyNote>();
        for (const n of teamNotes) {
          if (n?.id) merged.set(n.id, n);
        }
        for (const n of personalNotes) {
          if (n?.id) merged.set(n.id, n);
        }
        return Array.from(merged.values()).sort((a, b) => {
          if (Boolean(a.pinned) !== Boolean(b.pinned)) {
            return a.pinned ? -1 : 1;
          }
          return this.toMillis(b.createdAt) - this.toMillis(a.createdAt);
        });
      })
    );
  }

  async createNote(params: {
    companyId: string;
    loadId: string;
    content: string;
    visibility: StickyNoteVisibility;
    color: StickyNoteAccent;
    stopNumber?: number | null;
    pinned?: boolean;
  }): Promise<string> {
    const user = this.authService.user();
    const appUser = this.authService.appUser();
    if (!user?.uid) throw new Error('Must be signed in to add a sticky note.');

    const trimmed = params.content.trim();
    if (!trimmed) throw new Error('Sticky note cannot be empty.');

    const colRef = collection(this.firestore, `companies/${params.companyId}/loadStickyNotes`);
    const noteRef = doc(colRef);
    const authorName =
      appUser?.fullName ||
      user.displayName ||
      appUser?.contact?.email ||
      user.email ||
      'Team Member';

    await setDoc(noteRef, {
      id: noteRef.id,
      companyId: params.companyId,
      loadId: params.loadId,
      stopNumber: params.stopNumber ?? null,
      visibility: params.visibility,
      color: params.color,
      content: trimmed,
      pinned: Boolean(params.pinned),
      authorId: user.uid,
      authorName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return noteRef.id;
  }

  async updateNote(
    companyId: string,
    noteId: string,
    patch: Partial<Pick<LoadStickyNote, 'content' | 'visibility' | 'color' | 'pinned' | 'stopNumber'>>
  ): Promise<void> {
    const noteRef = doc(this.firestore, `companies/${companyId}/loadStickyNotes/${noteId}`);
    const payload: Record<string, any> = {
      ...patch,
      updatedAt: serverTimestamp(),
    };
    if (typeof payload['content'] === 'string') {
      payload['content'] = payload['content'].trim();
    }
    await updateDoc(noteRef, payload);
  }

  async deleteNote(companyId: string, noteId: string): Promise<void> {
    const noteRef = doc(this.firestore, `companies/${companyId}/loadStickyNotes/${noteId}`);
    await deleteDoc(noteRef);
  }
}
